/**
 * useExpandShortcut — listens for ⌘K / Ctrl+K and calls onExpand when:
 *   1. enabled is true (i.e. surface is in peek mode), AND
 *   2. focus is NOT in an editable field (input, textarea, [contenteditable]).
 *
 * FR-002: keyboard shortcut to expand conversation.
 * SC-006: must not fire when focus is in an editable field.
 *
 * Cleans up its event listener on unmount.
 */

import { useEffect } from 'react';

function isEditableTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea') return true;
  // Check both the DOM property (real browsers) and the HTML attribute
  // (jsdom does not reflect contentEditable as a property on all elements).
  const ceProperty = (el as HTMLElement).contentEditable;
  if (ceProperty === 'true') return true;
  if (el.getAttribute('contenteditable') === 'true') return true;
  return false;
}

/**
 * @param enabled   Whether the shortcut should be active (true = peek mode).
 * @param onExpand  Callback invoked when the shortcut fires.
 */
export function useExpandShortcut(enabled: boolean, onExpand: () => void): void {
  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      const isKKey = e.key === 'k' || e.key === 'K';
      const hasModifier = e.metaKey || e.ctrlKey;
      if (!isKKey || !hasModifier) return;
      if (isEditableTarget(document.activeElement)) return;

      e.preventDefault();
      onExpand();
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [enabled, onExpand]);
}
